import React, { useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { remarkChipPlugin } from '../../../utils/remarkChipPlugin';
import RatingStars from './RatingStars';
import ChatChip from '../../Atoms/ChatChip';
import { parseChips } from '../../../utils/formatters';
import { useTranslation } from 'react-i18next';
// Ya no necesitas formatText para los mensajes del bot
// import { formatText } from '../../../utils/formatters';
import './ChatMessage.css';

const MessageList = ({
    messages,
    isLoading,
    onActionClick,
    onWidgetSubmit,
    feedbackEnabled = false,
    onRatingClick,
    feedbackState = {},
    projects = [],
    handleProjectSelection,
    handleSendMessage
}) => {
    const messagesEndRef = useRef(null);
    const { t } = useTranslation();
    const mesaggesList = Array.isArray(messages) ? messages : [];
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [mesaggesList , isLoading]);

    const handleContainerClick = (e) => {

        if (e.target.closest('.governance-alert')) return;
        if (e.target.closest('.widget-form-container')) return;

        const target = e.target.closest('.chat-action-btn');
        if (!target) return;
        e.preventDefault();
        onActionClick(target.dataset.action, { name: target.dataset.name });
    };

    const handleWidgetSubmit = (e, actionType, messageId) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const data = Object.fromEntries(formData);
        onWidgetSubmit(actionType, data, messageId);
    };

    const handleGovernanceAction = (governance) => {
        // Opción 1: Navegar directamente
        window.location.href = '/dashboard'; // o usar history.push si lo pasas como prop

        // Opción 2: Disparar una acción personalizada
        // onActionClick?.('governance-wizard', { governance });
    };

    const shouldShowRating = (msg) => {
        if (!feedbackEnabled) return false;
        if (msg.sender !== 'bot') return false;
        if (msg.isError) return false;
        if (msg.sender === 'debug') return false;
        // if (msg.governance?.requires_wizard_action) return false;
        if (msg.widget?.type === 'form') return false;
        if (msg.type === 'welcome') return false;
        if (!msg.content || msg.content.length < 10) return false; // Mensajes muy cortos
        return true;
    };

    const lastBotMessageIndex = mesaggesList 
        .map((msg, idx) => (msg.sender === 'bot' ? idx : -1))
        .filter(idx => idx !== -1)
        .pop();

    const getMessageContent = (msg) => {
        // Si tiene translationKey, traducir en tiempo real
        if (msg.translationKey) {
            return t(msg.translationKey, msg.translationParams || {});
        }
        // Si no, retornar el contenido normal
        return msg.content;
    };

    return (
        <div className="" onClick={handleContainerClick}>
            {mesaggesList .length === 0 && !isLoading && (
                <div className="empty-state">
                    <h2>¡Welcome!</h2>
                    <p>Select a previous conversation or start a new one to begin</p>
                </div>
            )}

            {mesaggesList .map((msg, index) => {

                const messageContent = getMessageContent(msg);
                return (
                    <div key={msg.id} style={{ marginBottom: '15px' }}>

                        {messageContent  && (
                            <div className={`message ${msg.sender}-message ${msg.isError ? 'error' : ''}`}>
                                {msg.sender === 'bot' ? (
                                    <ReactMarkdown
                                        remarkPlugins={[remarkGfm, remarkChipPlugin]}
                                        components={{

                                            // ✅ Interceptar los chips generados por el plugin
                                            div: ({ node, children, className, ...props }) => {
                                                if (index !== lastBotMessageIndex || isLoading) {
                                                    return null; // O retornar el texto plano: <span>{props['data-chip-text']}</span>
                                                }
                                                if (className?.includes('chat-chips-stack')) {
                                                    const isDisabled = index !== lastBotMessageIndex;
                                                    return (
                                                        <div className={`chat-chips-stack ${isDisabled ? 'disabled' : ''}`}>
                                                            <div className="chips-stack-header">
                                                                <span className="chips-stack-icon"></span>
                                                                <span className="chips-stack-title">Suggested actions</span>
                                                            </div>
                                                            <div className="chips-stack-list">
                                                                {children}
                                                            </div>
                                                        </div>
                                                    );
                                                }

                                                // ✅ Renderizar chips individuales
                                                if (className?.includes('chat-chip-item')) {
                                                    return (
                                                        <ChatChip
                                                            text={props['data-chip-text']}
                                                            onClick={handleSendMessage}
                                                            disabled={index !== lastBotMessageIndex}
                                                            stacked={true}
                                                        />
                                                    );
                                                }

                                                return <div className={className} {...props}>{children}</div>;
                                            },
                                            // Tablas con scroll horizontal
                                            table: ({ node, ...props }) => (
                                                <div className="markdown-table-wrapper">
                                                    <table {...props} />
                                                </div>
                                            ),
                                            // Código inline vs bloques
                                            code: ({ node, inline, className, children, ...props }) => {
                                                return inline ? (
                                                    <code className="markdown-inline-code" {...props}>
                                                        {children}
                                                    </code>
                                                ) : (
                                                    <pre className="markdown-code-block">
                                                        <code className={className} {...props}>
                                                            {children}
                                                        </code>
                                                    </pre>
                                                );
                                            },
                                            // Links seguros
                                            a: ({ node, ...props }) => (
                                                <a {...props} target="_blank" rel="noopener noreferrer" />
                                            ),
                                            // Párrafos con detección de emojis
                                            p: ({ node, children, ...props }) => {
                                                const text = children?.toString() || '';
                                                const hasOnlyEmoji = /^[\p{Emoji}\s]+$/u.test(text);
                                                return (
                                                    <p className={hasOnlyEmoji ? 'markdown-emoji-large' : ''} {...props}>
                                                        {children}
                                                    </p>
                                                );
                                            }
                                        }}
                                    >
                                        {parseChips(messageContent)}
                                    </ReactMarkdown>
                                ) : (
                                    // ✅ Texto plano para mensajes del usuario
                                    <span>{messageContent}</span>
                                )}

                                {
                                    msg.governance?.requires_project_selection && (
                                        <div className="project-selection-container">
                                            <label htmlFor={`project-select-${msg.id}`} className="project-selection-label">
                                                Select a project to continue:
                                            </label>
                                            <div className="custom-select-wrapper">
                                                <select
                                                    id={`project-select-${msg.id}`}
                                                    className="custom-select premium-select"
                                                    onChange={(e) => {
                                                        const selectedProject = projects.find(p => p.id === parseInt(e.target.value));
                                                        if (selectedProject) {
                                                            handleProjectSelection(selectedProject);
                                                        }
                                                    }}
                                                    defaultValue=""
                                                    required
                                                >
                                                    <option value="" disabled>
                                                        Choose a project...
                                                    </option>
                                                    {projects.map((project) => (
                                                        <option key={project.id} value={project.id}>
                                                            {project.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    )}


                            </div>
                        )}


                        {msg.governance?.requires_wizard_action && (
                            <div className="governance-alert">
                                <div className="governance-header">
                                    <span className="governance-icon">⚠️</span>
                                    <strong className="governance-title">Action Required</strong>
                                </div>
                                <div className="governance-body">
                                    <p className="governance-description">
                                        {msg.governance.pcr_status === 'incomplete'
                                            ? `Your project is missing required information: ${msg.governance.missing_fields}`
                                            : 'Your project requires attention to continue'}
                                    </p>
                                    <button
                                        className="btn-governance-action"
                                        onClick={(e) => {
                                            e.stopPropagation(); // 🔴 Importante: evitar que el click se propague
                                            handleGovernanceAction(msg.governance);
                                        }}
                                    >
                                        Complete Project Setup
                                        <span className="btn-icon">→</span>
                                    </button>
                                </div>
                            </div>
                        )}

                        {shouldShowRating(msg) && (
                            <RatingStars
                                rating={feedbackState[msg.messageId]?.rating || 0}
                                onRatingClick={(rating) => onRatingClick?.(msg.messageId, rating)}
                                isRated={feedbackState[msg.messageId]?.hasRated || false}
                                disabled={feedbackState[msg.messageId]?.hasRated || false}
                            />
                        )}
                        {msg.widget && msg.widget.type === 'form' && (
                            <div className="widget-form-container">
                                {msg.widget.completed ? (
                                    <div className="widget-completed-state">
                                        <span>Data submitted successfully</span>
                                    </div>
                                ) : (
                                    <>
                                        <h4 style={{ marginTop: 0, marginBottom: '15px' }}>
                                            Please complete the project details
                                        </h4>
                                        <form onSubmit={(e) => handleWidgetSubmit(e, msg.widget.action, msg.id)}>
                                            {msg.widget.required_fields?.map(field => (
                                                <div key={field.id} className="widget-form-group">
                                                    {field.value ? (
                                                        <div className="prefilled-field">
                                                            <span title="Dato capturado por la IA">✅</span>
                                                            <span className="prefilled-label">{field.label}:</span>
                                                            <span className="prefilled-value">{field.value}</span>
                                                            <input type="hidden" id={field.id} name={field.id} value={field.value} />
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <label htmlFor={field.id}>{field.label}</label>
                                                            {field.options && field.options.length > 0 ? (
                                                                <select
                                                                    id={field.id}
                                                                    name={field.id}
                                                                    required={field.required !== false}
                                                                    className="widget-input"
                                                                    defaultValue=""
                                                                >
                                                                    <option value="" disabled>Selecciona una opción</option>
                                                                    {field.options.map(opt => (
                                                                        <option key={opt} value={opt}>{opt}</option>
                                                                    ))}
                                                                </select>
                                                            ) : (
                                                                <input
                                                                    type={field.type}
                                                                    id={field.id}
                                                                    name={field.id}
                                                                    required={field.required !== false}
                                                                    className="widget-input"
                                                                    placeholder={`Enter ${field.label.toLowerCase()}`}
                                                                    defaultValue=""
                                                                />
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            ))}
                                            <button type="submit" className="widget-submit-btn">
                                                Send
                                            </button>
                                        </form>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                )
            })}

            {isLoading && (
                <div className="message bot-message">
                    <div className="typing-indicator">
                        <span className="typing-dot"></span>
                        <span className="typing-dot"></span>
                        <span className="typing-dot"></span>
                    </div>
                </div>
            )}
            <div ref={messagesEndRef} />
        </div>
    );
};

export default MessageList;